/**
 * Cómo va el piloto.
 *
 * 🚨 Es la pregunta que decide si esto sirve o no. Dar acceso es fácil; lo
 * difícil es que el conductor **abra la app allá en la carretera**. Si se
 * reparten 48 claves y nadie entra, hay que enterarse en días, no en meses.
 *
 * ⭐ Y lo que de verdad cuenta no es haber entrado: es **haber registrado
 * entregas**. Quien entró una vez y no volvió, para la operación es igual que
 * si no tuviera la app. Por eso hay dos números distintos y no uno.
 *
 * 📊 Medido el 21-ago-2026 antes de construir esto: 8 con clave · 6 ya
 * entraron · 2 nunca · 3 siguen con la clave que les dictaron.
 */

import { useEffect, useState } from 'react'
import { pedirConSesion, puedeCambiar } from './Entrar.tsx'
import { cedula as conPuntos, comoFecha } from './formato.ts'

interface ConAcceso {
  id: string
  cedula: string
  nombre: string | null
  estado: string
  ultimo_acceso: string | null
  clave_sin_cambiar: boolean
  ve_en_la_app: number | string
  le_dieron_acceso: string | null
  en_calle: number | string
  entregas_registradas: number | string
  ultima_entrega: string | null
}

interface Resumen {
  conClave: number
  yaEntraron: number
  nuncaEntraron: number
  estaSemana: number
  claveSinCambiar: number
  venTrabajo: number
}

/** «hoy», «ayer», «hace 5 días». Lo que se lee de un vistazo. */
/*
 * 🚨 Las fechas se parsean con `comoFecha`, NUNCA con `new Date()` a pelo.
 *
 * Postgres devuelve `2026-08-20 19:23:50.737-05` y JavaScript **no sabe leer
 * ese `-05`**: el estándar pide `-05:00`. La fecha queda inválida y la pantalla
 * dice «Nunca» sobre alguien que sí entró — se vio así, con una fila diciendo
 * «Nunca entró» y al lado «Entró, pero no ha entregado».
 *
 * ⛔ El error estaba en CINCO pantallas porque cada vez volví a escribir la
 * función en vez de usar la que ya existía en `formato.ts`.
 */
function cuando(iso: string | null): string | null {
  if (!iso) return null
  const f = comoFecha(iso)
  if (!f) return null
  const dias = Math.floor((Date.now() - f.getTime()) / 86400000)
  if (dias < 1) return 'hoy'
  if (dias === 1) return 'ayer'
  if (dias < 31) return `hace ${dias} ${dias === 1 ? 'día' : 'días'}`
  return `hace ${Math.floor(dias / 30)} meses`
}

export function ComoVaElPiloto({
  alDarClave,
  generando,
}: {
  alDarClave: (cedula: string, nombre: string | null) => void
  generando: string | null
}) {
  const [lista, setLista] = useState<ConAcceso[] | null>(null)
  const [resumen, setResumen] = useState<Resumen | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** De quién se acaba de copiar el mensaje, para dar el visto. */
  const [copiado, setCopiado] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const r = await pedirConSesion<{ conductores: ConAcceso[]; resumen: Resumen }>(
          '/api/admin/conductores/con-acceso',
        )
        setLista(r.conductores)
        setResumen(r.resumen)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo cargar')
      }
    })()
  }, [])

  const n = (v: number | string) => Number(v ?? 0)

  if (error) return <p className="linea-msg linea-msg-mal">{error}</p>
  if (!lista || !resumen) return <p className="cargando">Cargando…</p>

  const usando = lista.filter((c) => n(c.entregas_registradas) > 0).length
  /*
   * 🚨 «Ya entraron» los cuenta a todos, incluidos los que se quedaron en la
   * pantalla de ponerse su clave — que NO pueden hacer nada. Contarlos como
   * avance del piloto lo pinta mejor de lo que va.
   */
  const aMedioCamino = lista.filter((c) => c.ultimo_acceso && c.clave_sin_cambiar).length
  /*
   * Los que pasaron todos los pasos y **nunca han podido hacer nada**: sin
   * viajes que ver y sin una sola entrega registrada. Se cuentan igual que en
   * la tabla, para que el embudo y las filas digan lo mismo.
   */
  const atascadosSinTrabajo = lista.filter(
    (c) =>
      c.ultimo_acceso &&
      !c.clave_sin_cambiar &&
      n(c.ve_en_la_app) === 0 &&
      n(c.entregas_registradas) === 0,
  ).length

  /*
   * 🚨🚨 EL EMBUDO, ARRIBA DE TODO.
   *
   * Los cuatro recuadros de abajo dicen cuántos hay en cada estado. Lo que no
   * decían es **dónde se cae la gente**, que es lo único que se puede accionar.
   *
   * MEDIDO el 24-ago-2026: 27 con clave → 25 entraron → 18 pasaron la clave →
   * **13 ven trabajo**. Entre el primero y el último se pierden catorce, y
   * ninguno de los dos escalones intermedios estaba a la vista.
   *
   * El último es el más cruel: cinco conductores recibieron la clave, entraron,
   * pusieron la suya… y la app les sale **sin un solo viaje**, porque los suyos
   * quedaron antes del corte o ya están cerrados. Desde su silla, la app «no
   * sirve» — y nadie en la oficina tenía cómo saberlo.
   */
  const embudo: { n: number; que: string; pierde?: string; ojo?: boolean }[] = [
    { n: resumen.conClave, que: 'tienen clave' },
    {
      n: resumen.yaEntraron,
      que: 'entraron',
      /* Singular y plural: es la primera frase que se lee de este tablero. */
      ...(resumen.nuncaEntraron > 0
        ? {
            pierde:
              resumen.nuncaEntraron === 1
                ? '1 no ha abierto la app'
                : `${resumen.nuncaEntraron} no han abierto la app`,
            ojo: true,
          }
        : {}),
    },
    {
      n: resumen.yaEntraron - aMedioCamino,
      que: 'pusieron su clave',
      ...(aMedioCamino > 0
        ? {
            pierde:
              aMedioCamino === 1
                ? '1 se quedó en esa pantalla'
                : `${aMedioCamino} se quedaron en esa pantalla`,
            ojo: true,
          }
        : {}),
    },
    {
      n: resumen.venTrabajo,
      que: 'ven trabajo hoy',
      /*
       * 🚨 LA PÉRDIDA CUENTA SOLO A QUIEN NUNCA HA PODIDO USARLA.
       *
       * Medido el 24-ago-2026: 5 conductores no ven trabajo hoy, pero **tres de
       * ellos ya registraron entregas antes** —1, 2 y 3— y hoy simplemente no
       * tienen carga. Eso es un día normal, no un conductor que se cayó del
       * camino.
       *
       * Contándolos como pérdida, el tablero exageraba el problema de 2 a 5. Y
       * un número que exagera es tan inútil como uno que esconde: la oficina
       * sale a llamar a gente que no tiene nada que resolver.
       */
      ...(atascadosSinTrabajo > 0
        ? {
            pierde:
              atascadosSinTrabajo === 1
                ? '1 nunca ha tenido un viaje que ver'
                : `${atascadosSinTrabajo} nunca han tenido un viaje que ver`,
            ojo: true,
          }
        : {}),
    },
  ]

  return (
    <>
      <div className="caja" style={{ marginBottom: 16, padding: 16 }}>
        <h3 className="text-[16px] font-semibold">Dónde se está cayendo la gente</h3>
        <p className="mt-1 text-[13px] text-tinta-suave">
          De la clave a ver su primer viaje hay cuatro pasos. Aquí se ve en cuál se quedan.
        </p>
        <div className="mt-3 flex flex-wrap items-start gap-x-2 gap-y-3">
          {embudo.map((e, i) => (
            <div key={e.que} className="flex items-start gap-2">
              {i > 0 && (
                <span aria-hidden="true" className="mt-2 text-[18px] text-[#CFD4DE]">
                  ›
                </span>
              )}
              <div style={{ minWidth: 128 }}>
                <span className="num text-[26px] font-bold leading-none">{e.n}</span>
                <span className="mt-0.5 block text-[14px]">{e.que}</span>
                {e.pierde && (
                  <span
                    className="mt-0.5 block text-[13px] font-semibold"
                    style={{ color: '#CA2B2B' }}
                  >
                    − {e.pierde}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="kpis kpis-4">
        <article className="kpi">
          <p className="kpi-et">Tienen clave</p>
          <p className="kpi-n num">{resumen.conClave}</p>
          <p className="kpi-pie">Se les entregó el acceso</p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Ya entraron</p>
          <p className="kpi-n num">{resumen.yaEntraron}</p>
          <p className="kpi-pie">
            {resumen.estaSemana} esta semana
            {aMedioCamino > 0 && (
              <>
                {' · '}
                <strong style={{ color: '#CA2B2B' }}>
                  {aMedioCamino === 1
                    ? '1 se quedó a medio camino'
                    : `${aMedioCamino} se quedaron a medio camino`}
                </strong>
              </>
            )}
          </p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Nunca han entrado</p>
          {/* Rojo: son a los que hay que llamar hoy (regla 23). */}
          <p
            className="kpi-n num"
            style={resumen.nuncaEntraron > 0 ? { color: '#CA2B2B' } : undefined}
          >
            {resumen.nuncaEntraron}
          </p>
          <p className="kpi-pie">Tienen la clave y no la han usado</p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Entregando con la app</p>
          <p className="kpi-n num">{usando}</p>
          {/*
            🚨🚨 ESTE NÚMERO INCLUYE LAS PRUEBAS, y hay que decirlo.

            MEDIDO el 24-ago-2026: 19 conductores figuran «entregando con la
            app» con 61 entregas… y al mirar quién recibió salen
            **PRUEBA CELULAR LENTO**, **PEDRO SIN SENAL**, **PRUEBA FLUJO
            COMPLETO**. Son las pruebas que se hicieron desde las cuentas de
            esos conductores, no entregas de verdad.

            ⛔ Nada se borra —esta base es el archivo histórico— así que el
            número seguirá subido. Lo que se arregla es que **no engañe**: un
            tablero que cuenta pruebas como si fueran uso real hace creer que
            el piloto arrancó cuando todavía no.

            Es lo mismo que se hizo con las tres reglas de la pantalla de
            salud: ponerle la fecha para poder separar lo de antes de lo que
            está pasando.
          */}
          <p className="kpi-pie">
            Entrar no basta: la señal que cuenta es que registre entregas.
            <strong style={{ color: '#A4600D' }}>
              {' '}
              Ojo: incluye las pruebas que se hicieron antes de arrancar.
            </strong>
          </p>
        </article>
      </div>

      <div className="tabla-caja tabla-scroll">
        <table>
          <thead>
            <tr>
              <th>Conductor</th>
              <th>Le dieron acceso</th>
              <th>Última vez que entró</th>
              <th>Entregas con la app</th>
              <th>Cómo va</th>
              <th style={{ textAlign: 'right' }}>Clave</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((c) => {
              const entro = !!c.ultimo_acceso
              const entregas = n(c.entregas_registradas)
              return (
                <tr key={c.id}>
                  <td>
                    {/*
                      🚨 El NOMBRE, no la cedula. Esta es la pantalla donde se
                      decide A QUIEN LLAMAR —«hay que llamarlo»—, y a nadie se le
                      llama por su numero de cedula. El servidor ya lo mandaba;
                      se quedo sin pintar cuando llegaron los nombres.
                    */}
                    {c.nombre ? (
                      <>
                        <span style={{ fontWeight: 600 }}>{c.nombre}</span>
                        <small className="num">{conPuntos(c.cedula)}</small>
                      </>
                    ) : (
                      <span className="num" style={{ fontWeight: 600 }}>
                        {conPuntos(c.cedula)}
                      </span>
                    )}
                    {n(c.en_calle) > 0 && <small>Rodando ahora</small>}
                  </td>
                  <td>{cuando(c.le_dieron_acceso) ?? '—'}</td>
                  <td>{cuando(c.ultimo_acceso) ?? <span className="et et-mal">Nunca</span>}</td>
                  <td>
                    <span className="num">{entregas}</span>
                    {c.ultima_entrega && <small>la última {cuando(c.ultima_entrega)}</small>}
                  </td>
                  <td>
                    {/*
                     * 🚨 Entró y NO cambió su clave = está TRANCADO, no lento.
                     *
                     * Comprobado contra el servidor: mientras no ponga su propia
                     * clave, la app le responde que no a todo —no ve ni un
                     * viaje—. Se queda en la pantalla de «Ponle tu propia
                     * clave» y de ahí no pasa.
                     *
                     * Decía «Entró, pero no ha entregado» con una notita
                     * debajo. La oficina lo leía como alguien que va lento y no
                     * lo llamaba. Y no le sirve una clave nueva: le sirve que
                     * alguien le diga que TERMINE de ponerse la suya.
                     */}
                    {!entro ? (
                      <span className="et et-mal">Hay que llamarlo</span>
                    ) : c.clave_sin_cambiar ? (
                      <>
                        <span className="et et-mal">Se quedó a medio camino</span>
                        <small>
                          Entró y no terminó de ponerse su clave. Hasta que lo haga no ve ni un
                          viaje. No necesita clave nueva.
                        </small>
                        {/*
                          🚨🚨 EL MENSAJE LISTO, no «llámalo».
                          
                          Son **7 conductores** los que están así —medido el
                          24-ago-2026— y decirle a la oficina «llámalos» es
                          pedirle siete llamadas. La mayoría no se hacen.
                          
                          Lo que necesitan es una frase: **vuelve a abrir la app
                          y termina de ponerte tu clave**. Y de paso las reglas,
                          que es en lo que se atascaron: probar 1234, 0000 y los
                          últimos de la cédula, que se rechazan las tres.
                          
                          ⛔ NO genera clave nueva, a propósito. La que tiene le
                          sirve — lo que le falta es el segundo paso. Generarle
                          otra lo devolvería al mismo sitio.
                        */}
                        <button
                          onClick={() => void copiarRecordatorio(c.nombre, setCopiado)}
                          className="bmas"
                          style={{ marginTop: 6 }}
                        >
                          {copiado === c.nombre ? '✓ Copiado' : 'Copiar mensaje para él'}
                        </button>
                      </>
                    ) : n(c.ve_en_la_app) === 0 && entregas === 0 ? (
                      <>
                        {/*
                          🚨 ESTE NO VA LENTO: NO TIENE NADA QUE VER.
                          
                          Medido el 24-ago-2026: 5 conductores hicieron todo
                          bien —clave, entrar, poner la suya— y la app les sale
                          **sin un solo viaje**, porque los suyos quedaron antes
                          del corte o ya están cerrados. Desde su silla, la app
                          «no sirve».
                          
                          Salían como «Entró, pero no ha entregado», igual que
                          quien va lento. Y el consejo es el CONTRARIO: a este
                          no hay que llamarlo para que use la app — hay que
                          darle trabajo, o moverle el corte.
                        */}
                        <span className="et et-esp">Abre la app y no ve nada</span>
                        <small>
                          Hizo todo bien, pero no tiene viajes que ver: los suyos quedaron antes
                          del corte o ya están cerrados. No lo llames — asígnale carga o mueve la
                          fecha de arriba.
                        </small>
                      </>
                    ) : entregas === 0 ? (
                      <>
                        <span className="et et-esp">Entró, pero no ha entregado</span>
                        <small>
                          Ve {n(c.ve_en_la_app)}{' '}
                          {n(c.ve_en_la_app) === 1 ? 'viaje' : 'viajes'} en la app y todavía no ha
                          registrado ninguna entrega.
                        </small>
                      </>
                    ) : (
                      <span className="et et-ok">Usando la app</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {puedeCambiar() && (
                      <button
                        onClick={() => alDarClave(c.cedula, c.nombre)}
                        disabled={generando === c.cedula}
                        className="btn-tabla"
                      >
                        {generando === c.cedula ? 'Generando…' : 'Generar otra'}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

/**
 * El mensaje para quien se quedó trancado en la pantalla de la clave.
 *
 * 🚨 Distinto del de bienvenida: este NO lleva clave, porque ya tiene una que
 * sirve. Lo que le falta es el segundo paso, y las reglas con las que se
 * atascó —medido: 7 conductores ahí, uno lleva dos días—.
 */
async function copiarRecordatorio(
  nombre: string | null,
  setCopiado: (v: string | null) => void,
): Promise<void> {
  const corto = nombre?.trim().split(/\s+/)[0] ?? ''
  const texto = [
    `${corto ? corto + ', te' : 'Te'} falta un paso para ver tus viajes en SymTrack.`,
    '',
    `Abre la app: ${location.host}/app`,
    'Entra con tu cédula y la clave que te dimos, y ponle una tuya de 4 números.',
    '',
    'Ojo: no sirven 1234, 0000, todos iguales ni los 4 últimos de tu cédula.',
    'Escoge otros 4 que solo sepas tú. Hasta que la pongas no ves ni un viaje.',
    '',
    'No necesitas clave nueva: la que tienes sirve.',
  ].join(String.fromCharCode(10))

  try {
    await navigator.clipboard.writeText(texto)
    setCopiado(nombre)
    setTimeout(() => setCopiado(null), 4000)
  } catch {
    /* Portapapeles bloqueado: se le muestra para copiar a mano. */
    window.prompt('Copia este mensaje y mándaselo:', texto)
  }
}
